/**
 * The bulk-action bar above a selectable line table: "N products selected", a way to clear the
 * selection, and the actions that apply to the selected rows. Below the `lg` breakpoint it renders
 * `./ActionBarMobile.tsx` (actions in a "More Tools" popover); at `lg` and above,
 * `./ActionBarDesktop.tsx` (actions inline on the right).
 *
 * Position:
 * - Used by the cart (`components/ShoppingCart/ShoppingCartDesktop.tsx`,
 *   `components/ShoppingCart/ShoppingCartHeader/ShoppingCartHeader.tsx`, items built in
 *   `components/ShoppingCart/ShoppingCartBulkActionMenu/useShoppingCartActionItems.ts`) and by
 *   quote checkout
 *   (`components/Checkout/ExpressCheckout/QuoteCheckoutActionBar/QuoteCheckoutActionBar.tsx`).
 * - The caller owns the selection and the actions; this component only displays them. Item and prop
 *   shapes are in `./ActionBarTypes.ts`, labels in `./ActionBarContent.ts`.
 * - The forwarded ref reaches only the mobile view's `Popover`, so a caller can close the "More
 *   Tools" menu after an action runs (`useShoppingCartActionItems` → `popperConfigRef`).
 *
 * The older per-item renderer used by quote details, search and shopping lists is
 * `components/Shared/ActionItems/ActionItems.tsx`.
 *
 * Related: `unit-tests/shared/ActionBar.spec.tsx`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import ActionBarDesktop from "./ActionBarDesktop";
import ActionBarMobile from "./ActionBarMobile";
import { ActionBarProps } from "./ActionBarTypes";
import { ActionBarContent } from "./ActionBarContent";
import { PopperExposedHandlers } from "@ryerson/frontend.modal";

const ActionBarInner: React.ForwardRefRenderFunction<PopperExposedHandlers, ActionBarProps> = (
	props,
	ref
) => {
	const {
		localization: { language },
	} = useApplication();
	const content = ActionBarContent[language || "en"];

	return (
		<>
			<Media lessThan="lg">
				<ActionBarMobile ref={ref} {...props} content={content} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<ActionBarDesktop {...props} content={content} />
			</Media>
		</>
	);
};

/**
 * Bulk-action bar for a selectable table; picks the mobile or desktop view by breakpoint. The ref
 * exposes the mobile "More Tools" popover's `close` / `open` handlers.
 */
const ActionBar = React.forwardRef(ActionBarInner);

export default ActionBar;
